<template>
  <div class="card">
    <!-- Filtering Section -->
    <div class="dropdown-item date-item mt-4">
      <div class="date-grid">
        <div class="date-search-container">
          <div class="row p-1">
            <div class="col-md-2 form-group">
              <label for="orderDatePicker">Loading Date</label>
              <input type="date" v-model="orderDate" class="search-input" />
            </div>
            <div class="col-md-2 form-group">
              <label for="distributor">Distributor</label>
              <select v-model="distributor" id="distributor" class="search-input">
                <option value="">All</option>
                <option
                    v-for="distributor in distributors"
                    :key="distributor.profile_id"
                    :value="distributor.profile_id"
                >
                  {{ distributor.name }}
                </option>
              </select>
            </div>
            <div v-if="plantId === '0'" class="col-md-2 form-group">
              <label for="plant">Plant</label>
              <select v-model="plant" id="plant" class="search-input">
                <option value="">All</option>
                <option
                  v-for="plant in plants"
                  :key="plant.id"
                  :value="plant.id"
                >
                  {{ plant.name }}
                </option>
              </select>
            </div>
            <div class="col-md-2 form-group d-flex align-items-end">
              <button type="button" class="btn button-default" @click="search">
                Search
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Loading Indicator -->
    <div v-if="loading" class="loading-spinner mt-3">
      <i class="fa fa-spinner fa-spin"></i> Loading...
    </div>

    <!-- Data Table -->
    <div class="mt-4">
      <div class="table-responsive">
        <table class="table">
          <thead>
          <tr class="border-bottom-primary">
            <th scope="col">Ref No</th>
            <th scope="col">Distributor</th>
            <th scope="col">Plant</th>
            <th scope="col">Loading Date</th>
            <th scope="col">Status</th>
            <th scope="col">Action</th>
          </tr>
          </thead>
          <tbody>
          <tr v-for="(item, index) in orderScheduleList" :key="index">
            <td>{{ item.refrence_no }}</td>
            <td>{{ item.business_name }}</td>
            <td>{{ item.name }}</td>
            <td>{{ item.loading_date_time }}</td>
            <td class="mt-1">
                <span
                    :class="getStatusLabelClass(item.status)"
                    v-html="getDispatchStatus(item.status)"
                ></span>
            </td>
            <td>
              <!-- Edit -->
<i
    v-if="item.is_grn !== '1' && item.is_grn !== '2' && item.is_rejected !== '1'"
    class="fa fa-edit"
    aria-hidden="true"
    @click="editDetails(item.id)"
></i>

              <!-- View -->
              <i
                  class="fa fa-eye action-icon"
                  aria-hidden="true"
                  @click="openOrderDetails(item.id)"
              ></i>
            </td>
          </tr>
          </tbody>
        </table>
      </div>
    </div>

    <!-- Reusable View Modal -->
    <OrderRequestScheduleView
      v-if="showOrderRequestScheduleModal"
      :orderId="selectedOrderId"
      @close="showOrderRequestScheduleModal = false"
    />
  </div>
</template>

<script>
import { useRouter } from "vue-router";
import {
  fetchDistributors,
  fetchPlantOrderRequest,
  fetchOrderScheduleList,
} from "@/services/dataService";
import OrderRequestScheduleView from "@/pages/Modal/viewOrderRequestSchedule.vue";

export default {
  components: { OrderRequestScheduleView },

  data() {
    const now = new Date();
    const currentDate = `${now.getFullYear()}-${String(
        now.getMonth() + 1
    ).padStart(2, "0")}-${String(now.getDate()).padStart(2, "0")}`;

    const nextDay = new Date(now);
    nextDay.setDate(nextDay.getDate() + 1);
    const orderDate = `${nextDay.getFullYear()}-${String(
        nextDay.getMonth() + 1
    ).padStart(2, "0")}-${String(nextDay.getDate()).padStart(2, "0")}`;

    const plantId = localStorage.getItem("plant_id");

    return {
      profile_id: localStorage.getItem("profile_id") || null,
      token: localStorage.getItem("token_set") || "",
      orderDate,
      currentDate,
      plantId,
      loading: false,
      orderScheduleList: [],
      distributor: "",
      plant: plantId === "0" ? "" : plantId,
      distributors: [],
      plants: [],
      errors: {},
      showOrderRequestScheduleModal: false,
      selectedOrderId: null,
    };
  },

  setup() {
    const router = useRouter();
    const editDetails = (id) => {
      router.push({ path: `/order-request-schedule-edit/${id}` });
    };
    return { editDetails };
  },

  methods: {
    getStatusLabelClass(status) {
      if (status === "Completed") return "status-completed";
      if (status === "Pending") return "status-pending";
      if (status === "Rejected") return "status-rejected";
      if (status === "Scheduled") return "status-scheduled";
      return "status-unknown";
    },

    getDispatchStatus(status) {
      if (status === "Completed")
        return '<span class="status-completed">Completed</span>';
      if (status === "Pending")
        return '<span class="status-pending">Pending</span>';
      if (status === "Rejected")
        return '<span class="status-rejected">Rejected</span>';
      if (status === "Scheduled")
        return '<span class="status-scheduled">Scheduled</span>';
      return '<span class="status-unknown">Unknown</span>';
    },

    async fetchDistributors() {
      try {
        const response = await fetchDistributors(this.profile_id, this.token);
        if (response.data.status === "success") {
          this.distributors = response.data.data;
        } else {
          this.errors.distributors = response.data.message;
        }
      } catch (error) {
        this.errors.distributors = "Failed to fetch distributors.";
      }
    },

    async fetchPlant() {
      try {
        const response = await fetchPlantOrderRequest(this.token);
        if (response.data.status === "success") {
          this.plants = response.data.data;
        } else {
          this.errors.plants = response.data.message;
        }
      } catch (error) {
        this.errors.plants = "Failed to fetch plants.";
      }
    },

    async fetchOrderScheduleList() {
      this.loading = true;
      this.orderScheduleList = [];
      try {
        const payload = {
          distributor_id: this.distributor,
          formdate: this.orderDate,
          gas_plant_id: this.plant,
        };
        const response = await fetchOrderScheduleList(this.token, payload);
        if (response.data.status === "success") {
          // Filter data based on plantId
          if (this.plantId === "0") {
            // Show all statuses when plantId is 0
            this.orderScheduleList = response.data.data;
          } else {
            // Show only scheduled status when plantId is not 0
            this.orderScheduleList = response.data.data.filter(item =>
                item.status === "Scheduled"
            );
          }
        } else {
          this.errors.orderSchedule = response.data.message;
        }
      } catch (error) {
        this.errors.orderSchedule = "Failed to fetch order schedule.";
      } finally {
        this.loading = false;
      }
    },

    search() {
      this.fetchOrderScheduleList();
    },

    openOrderDetails(orderId) {
      this.selectedOrderId = orderId;
      this.showOrderRequestScheduleModal = true;
    },
  },

  mounted() {
    this.fetchDistributors();
    this.fetchPlant();
    this.fetchOrderScheduleList();
  },
};
</script>

<style scoped>
/* Styling for labels */
label {
  display: block;
  margin-bottom: 5px;
  font-weight: bold;
  font-size: 14px; /* Adjust font size for better readability */
}

/* Styling for select elements */
select {
  width: 100%; /* Ensure select takes full width of its container */
  padding: 15px; /* Increase padding for better spacing */
  border-radius: 5px;
  border: 1px solid #ccc;
  font-size: 14px; /* Adjust font size for better readability */
  text-align: left; /* Left align the text */
  transition: border-color 0.3s, box-shadow 0.3s;
}

/* Focus styling for select elements */
select:focus {
  border-color: #007bff;
  box-shadow: 0 0 5px rgba(38, 143, 255, 0.5);
  outline: none;
}

/* Styling for search input */
.search-input {
  flex: 1;
  padding: 10px;
  border-radius: 5px;
  border: 1px solid #ccc;
  font-size: 14px; /* Adjust font size for better readability */
  transition: border-color 0.3s, box-shadow 0.3s;
}

/* Focus styling for search input */
.search-input:focus {
  border-color: #007bff;
  box-shadow: 0 0 5px rgba(38, 143, 255, 0.5);
  outline: none; /* Remove the default outline */
}

/* Styling for search button */
.search-container {
  display: flex;
  align-items: center;
  justify-content: center;
}

/* Table styling */
.table-container {
  width: 100%;
  margin-top: 20px;
}

.table {
  width: 100%;
  border-collapse: collapse;
}

.table th, .table td {
  padding: 10px;
  text-align: left;
  border-bottom: 1px solid #ddd;
}

.table th {
  background-color: #f9f9f9;
  font-weight: bold;
}

.table td {
  background-color: #ffffff;
}

/* Popup styling */
.popup-content h3 {
  margin-top: 0;
}

.popup-content ul {
  list-style: none;
  padding: 0;
}

.popup-content li {
  margin-bottom: 10px;
}

.popup-content hr {
  border: 0;
  border-top: 1px solid #ddd;
  margin: 10px 0;
}

/* Optional: Add spacing and icon styling for the complaint messages */
.table td i.fa-flag {
  margin-right: 8px; /* Space between icon and text */
  color: red; /* Color of the flag icon */
}

/* Styling for the "eye" icon */
.fa-trash {
  cursor: pointer;
  transition: color 0.3s, transform 0.3s; /* Smooth transition for color and transform */
}

/* Hover effect for the "eye" icon */
.fa-trash:hover {
  color: #ee164d; /* Darker shade on hover */
  transform: scale(1.2); /* Slightly enlarge the icon on hover */
}

/* Active effect for the "eye" icon */
.fa-trash:active {
  transform: scale(1.1); /* Slightly shrink the icon on click */
}

/* Status color classes */
.status-pending {
  background-color: #FFC107; /* Yellow color */
  color: #FFFFFF; /* White text */
  padding: 5px 10px;
  border-radius: 5px;
}
.status-completed {
  background-color: #069E27; /* Green color */
  color: #FFFFFF; /* White text */
  padding: 5px 10px;
  border-radius: 5px;
}
.status-scheduled {
  background-color: #1B6CB4; /* Blue color */
  color: #FFFFFF; /* White text */
  padding: 5px 10px;
  border-radius: 5px;
}

/* Loading spinner styles */
.loading-spinner {
  font-size: 18px;
  text-align: center;
  margin-top: 20px;
  color: #007bff; /* Preserved color for spinner */
}

/* New styles for the remark section */
.remark-section {
  margin-top: 20px;
}

.remark-section label {
  font-weight: bold;
  font-size: 14px;
}

.remark-section textarea {
  width: 100%;
  padding: 10px;
  border-radius: 5px;
  border: 1px solid #ccc;
  font-size: 14px;
  transition: border-color 0.3s, box-shadow 0.3s;
}

.remark-section textarea:focus {
  border-color: #007bff;
  box-shadow: 0 0 5px rgba(38, 143, 255, 0.5);
  outline: none;
}

.remark-section textarea {
  width: 100%;
  padding: 10px 20px; /* Add padding to the right and left */
  border-radius: 5px;
  border: 1px solid #ccc;
  font-size: 14px;
  transition: border-color 0.3s, box-shadow 0.3s;
}

.fa-eye {
  cursor: pointer;
  transition: color 0.3s, transform 0.3s;
  margin-left: 5px;
}

.fa-eye:hover {
  color: #0056b3;
  transform: scale(1.2);
}

.fa-eye:active {
  transform: scale(1.1);
}

.fa-edit {
  cursor: pointer;
  transition: color 0.3s, transform 0.3s;
}

.fa-edit:hover {
  color: #0056b3;
  transform: scale(1.2);
}

.fa-edit:active {
  transform: scale(1.1);
}

/* Modal Overlay Style */
.modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-color: rgba(0,0,0,0.5);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 1000;
}




















/* Popup modal */
.modal {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-color: rgba(0, 0, 0, 0.5);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 1000;
}
.popup-content {
  background-color: white;
  padding: 20px;
  border-radius: 5px;
  width: 800px;
  max-width: 100%;
  max-height: 80vh; /* Set a maximum height */
  overflow-y: auto; /* Enable vertical scrolling */
  box-shadow: 0 5px 15px rgba(0, 0, 0, 0.3);
}
.popup-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
}
.close-btn {
  background: none;
  border: none;
  font-size: 24px;
  cursor: pointer;
  color: #000;
  transition: color 0.3s;
}
.popup-content h3 {
  margin-top: 0;
}
.popup-content ul {
  padding-left: 20px;
}
.popup-content ul li {
  margin-bottom: 10px;
}
.popup-table {
  max-height: 300px; /* Adjust the height as needed */
  overflow-y: auto;  /* Enable vertical scrolling */
}
.icon-disabled {
  color: #cccccc;
  cursor: not-allowed;
  pointer-events: none;
  opacity: 0.5;
}
</style>